import { Card, Colors, Heading, Icon, SegmentedProgressBar, Text } from '@amelio/ui-library';

import { NextSteps } from './NextSteps';
import { ResultsPlaceholder } from './ResultsPlaceholder';

import type { BelowThresholdState } from './belowThresholdState';

import { useAmelioTranslations } from '@/hooks';

interface BelowThresholdResultsProps {
    state: BelowThresholdState;
    revieweeId: string;
}

/** An anonymous feedback below its minimum of responses: what is missing, never what was answered. */
export const BelowThresholdResults = ({ state, revieweeId }: BelowThresholdResultsProps) => {
    const { t } = useAmelioTranslations();
    const isClosed = state.kind === 'closed';
    const presentation = presentationOf(state, t);

    return (
        <Card
            padding={'none'}
            borderRadius={'2xl'}
            className={
                'relative mt-8 min-h-130 items-center justify-center overflow-hidden px-4 py-6 md:px-16 md:py-14'
            }
            dataTestId={'below-threshold-results'}
        >
            <ResultsPlaceholder isClosed={isClosed} />

            <Card
                variant={'panel'}
                shadow={'lg'}
                padding={'none'}
                borderRadius={'2xl'}
                zIndex={1}
                className={
                    'w-full max-w-130 items-center gap-3.5 px-5 py-7 text-center md:px-10 md:py-9'
                }
            >
                <span
                    className={`flex h-13 w-13 items-center justify-center rounded-full ${presentation.lockClassName}`}
                >
                    <Icon
                        name={'security-lock'}
                        size={'lg'}
                        color={presentation.lockColor}
                        ariaHidden
                    />
                </span>

                <Heading
                    level={3}
                    align={'center'}
                >
                    {presentation.title}
                </Heading>

                <SegmentedProgressBar
                    value={(state.count / state.minimum) * 100}
                    segmentCount={state.minimum}
                    ariaLabel={t('results.belowThreshold.received', {
                        count: state.count,
                        minimum: state.minimum,
                    })}
                    className={`w-75 max-w-full ${presentation.barClassName}`}
                />

                <Text
                    color={Colors.BASE_600}
                    align={'center'}
                >
                    {presentation.summary}
                </Text>

                <Text
                    size={'small'}
                    color={Colors.BASE_400}
                    align={'center'}
                    className={'max-w-115'}
                >
                    {presentation.explanation}
                </Text>

                {isClosed && <NextSteps revieweeId={revieweeId} />}
            </Card>
        </Card>
    );
};

/** What tells a feedback still collecting from one closed short of its minimum: its colors and its words. */
const presentationOf = (
    state: BelowThresholdState,
    t: ReturnType<typeof useAmelioTranslations>['t'],
) => {
    const counts = { count: state.count, minimum: state.minimum };

    if (state.kind === 'waiting') {
        return {
            lockClassName: 'bg-secondary-25',
            lockColor: Colors.SECONDARY,
            barClassName: '',
            title: t('results.belowThreshold.waitingTitle', { count: state.minimum - state.count }),
            summary: t('results.belowThreshold.received', counts),
            explanation: t('results.belowThreshold.explanation', { minimum: state.minimum }),
        };
    }

    return {
        lockClassName: 'bg-base-100',
        lockColor: Colors.BASE_400,
        barClassName: 'grayscale',
        title: t('results.belowThreshold.closedTitle'),
        summary: state.closedOn
            ? t('results.belowThreshold.closedOnWith', { ...counts, date: state.closedOn })
            : t('results.belowThreshold.closedWith', counts),
        explanation:
            state.count === 0
                ? t('results.belowThreshold.closedNoResponseExplanation')
                : t('results.belowThreshold.closedExplanation', { minimum: state.minimum }),
    };
};
